import React, { useCallback, useRef, useState } from 'react'
import clsx from 'clsx'
import styled from 'styled-components'
import HiddenInput from '../client/components/Common/Form/Input/HiddenInput'
import InputText from './Typography/InputText'
import LineMinusIcon from './Icons/line/LineMinusIcon'
import LinePlusIcon from './Icons/line/LinePlusIcon'
import { rem } from 'polished'
import noop from 'lib/function/noop'
import IconButton from 'LuxKit/Button/IconButton'

// Create an IconButtonWithPadding styled-component for consistent horizontal button padding
const IconButtonWithPadding = styled(IconButton)`
  width: auto;
  padding: ${rem(6)};

  &.size-large {
    padding: ${rem(8)};
  }
`

const CounterContainer = styled.div`
  display: inline-flex;
  align-items: center;
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-radius: ${props => props.theme.borderRadius.S};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  position: relative;
  transition: border 0.2s;
  &.invalid {
    border-color: ${props => props.theme.palette.messaging.critical.normalForeground};
  }
`

const CounterText = styled(InputText)`
  min-width: ${rem(24)};
  transition: color 0.2s;

  &.disabled {
    color: ${props => props.theme.palette.neutral.default.four};
  }
`

export type CounterChangeEventHandler = (value: number, inputElement: HTMLInputElement | null, event: React.MouseEvent<HTMLButtonElement>) => void

interface Props extends Utils.PropsWithDataAttributes {
  defaultValue?: number
  value?: number
  min?: number
  max?: number
  name?: string
  onChange?: CounterChangeEventHandler
  disabled?: boolean
  className?: string
  invalidMessage?: string
  /** @default large */
  size?: 'small' | 'large'
}

function Counter(props: Props) {
  const {
    className,
    name,
    min = 0,
    max = Number.MAX_SAFE_INTEGER,
    defaultValue,
    value,
    onChange = noop,
    disabled,
    invalidMessage,
    'data-testid': dataTestId,
    size = 'large',
    ...inputProps
  }: Props = props
  const [isDirty, setDirty] = useState<boolean>(false)
  const [error, setError] = useState<boolean>(false)
  const [internalValue, setValue] = useState<number>(defaultValue ?? 0)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const isControlled = value !== undefined
  const onDecrement = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    if (isControlled) {
      onChange(value - 1, inputRef.current, e)
      return
    }

    setValue(prevValue => {
      const nextValue = prevValue - 1

      if (nextValue >= min) {
        onChange(nextValue, inputRef.current, e)
        return nextValue
      }

      return prevValue
    })
  }, [isControlled, onChange, value, min])

  const onIncrement = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    if (isControlled) {
      onChange(value + 1, inputRef.current, e)
      return
    }

    setValue(prevValue => {
      const nextValue = prevValue + 1

      if (nextValue <= max) {
        onChange(nextValue, inputRef.current, e)
        return nextValue
      }
      return prevValue
    })
  }, [isControlled, onChange, value, max])

  const onErrorUpdate = useCallback((message?: string) => {
    setError(!!message)
  }, [])

  const actualValue = isControlled ? value : internalValue

  return (<CounterContainer
    className={clsx(className, `size-${size}`, { invalid: (isDirty && error) })}
    data-testid={dataTestId}
  >
    <HiddenInput
      {...inputProps}
      type="number"
      ref={inputRef}
      min={min}
      max={max}
      value={actualValue}
      onDirtyChange={setDirty}
      onErrorUpdate={onErrorUpdate}
      name={`${name}::number`}
      invalidMessage={invalidMessage}
    />
    <IconButtonWithPadding
      kind="tertiary"
      size={size}
      disabled={disabled || actualValue <= min}
      variant="dark"
      onClick={onDecrement}
      data-testid={dataTestId ? `${dataTestId}-decrement` : undefined}
    >
      <LineMinusIcon />
    </IconButtonWithPadding>
    <CounterText onClick={e => e.preventDefault()} align="center" variant="text-regular" className={clsx({ disabled })}>{actualValue}</CounterText>
    <IconButtonWithPadding
      kind="tertiary"
      size={size}
      disabled={disabled || actualValue >= max}
      variant="dark"
      onClick={onIncrement}
      data-testid={dataTestId ? `${dataTestId}-increment` : undefined}
    >
      <LinePlusIcon />
    </IconButtonWithPadding>

  </CounterContainer>)
}

export default React.memo(Counter)
